import { useFetchApi } from '@/shared/utils/fetch'
import { tauriMode } from '@/shared/utils/import-meta'
import { getFullURL } from '@/shared/utils/url'

const AUDIO_MIME_BY_EXTENSION: Record<string, string> = {
  webm: 'audio/webm',
  m4a: 'audio/mp4',
  mp4: 'audio/mp4',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  opus: 'audio/ogg',
}

const blobByCookie = new Map<string, Blob>()
const objectUrlByCookie = new Map<string, string>()

const getExtension = (value: string): string => {
  const path = value.split('?')[0] ?? value
  return path.split('.').at(-1)?.toLowerCase() ?? ''
}

const toVoiceFilePath = (fileCookie: string): string => {
  if (fileCookie.startsWith('/files/')) return fileCookie
  return `/files/${fileCookie}`
}

const loadVoiceFile = async (path: string): Promise<Blob> => {
  if (tauriMode()) {
    const response = await useFetchApi().get<Blob>(path, {
      responseType: 'blob',
    })
    return response.data
  }

  const response = await fetch(getFullURL(path))
  if (!response.ok) {
    throw new Error(`Failed to load voice message: ${response.status}`)
  }
  return response.blob()
}

export const toPlayableAudioBlob = (blob: Blob, fileCookie = ''): Blob => {
  const type = blob.type.toLowerCase()
  if (type.startsWith('audio/')) return blob

  const mime = AUDIO_MIME_BY_EXTENSION[getExtension(fileCookie)] ?? 'audio/webm'
  return blob.slice(0, blob.size, mime)
}

export const loadVoiceFileBlob = async (fileCookie: string): Promise<Blob> => {
  const cached = blobByCookie.get(fileCookie)
  if (cached) return cached
  if (!fileCookie) {
    throw new Error('Missing voice file cookie')
  }

  const blob = toPlayableAudioBlob(await loadVoiceFile(toVoiceFilePath(fileCookie)), fileCookie)
  blobByCookie.set(fileCookie, blob)
  return blob
}

export const getPlayableVoiceObjectUrl = async (fileCookie: string): Promise<string> => {
  const cached = objectUrlByCookie.get(fileCookie)
  if (cached) return cached

  const objectUrl = URL.createObjectURL(await loadVoiceFileBlob(fileCookie))
  objectUrlByCookie.set(fileCookie, objectUrl)
  return objectUrl
}

export const revokePlayableVoiceObjectUrls = () => {
  objectUrlByCookie.forEach((objectUrl) => URL.revokeObjectURL(objectUrl))
  objectUrlByCookie.clear()
  blobByCookie.clear()
}
